import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiUsers,
  FiPhoneCall,
  FiMessageSquare,
  FiCalendar,
  FiClipboard,
  FiDollarSign,
  FiSettings,
  FiTag,
  FiBarChart2,
  FiCheckCircle,
} from "react-icons/fi";

const ModuleCRM = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - CRM Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>CRM | RedERP</title>
        <meta
          name="description"
          content="Centralize your leads, manage relationships, and grow sales with RedERP's CRM module. Track every customer touchpoint from first contact to closed deal."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10 relative">
          {/* Text Section */}
          <div className="flex-1 text-left mt-16">
            <h1 className="text-4xl font-bold mb-4">CRM – Customer Relationship Management</h1>
            <p className="mb-6 text-sm sm:text-base">
              RedERP CRM empowers your team to attract leads, convert customers, and nurture long-term loyalty — all in one place.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Start Your Free Trial
            </a>
          </div>

          {/* Illustration */}
          <div className="flex-1 hidden md:flex justify-end mt-12 mb-0">
            <img
              src="/assets/images/pos2.png"
              alt="CRM Illustration"
              className="w-[420px] lg:w-[420px] xl:w-[420px] h-[310px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* CRM Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left">
          {[
            {
              icon: <FiUsers size={24} />,
              title: "Lead Management",
              desc: "Capture, track, and assign leads. Automatically move them through your sales funnel.",
            },
            {
              icon: <FiClipboard size={24} />,
              title: "Deal & Pipeline Tracking",
              desc: "Visualize your entire pipeline, forecast revenue, and manage each stage with confidence.",
            },
            {
              icon: <FiPhoneCall size={24} />,
              title: "Contact History",
              desc: "Keep a log of calls, emails, meetings, and tasks linked to each customer or prospect.",
            },
            {
              icon: <FiMessageSquare size={24} />,
              title: "Follow-Ups & Reminders",
              desc: "Schedule calls, set auto-reminders, and get notified to follow up before deals go cold.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Activity Planning",
              desc: "Plan meetings, demos, and callbacks with built-in scheduling tools.",
            },
            {
              icon: <FiDollarSign size={24} />,
              title: "Quotation Management",
              desc: "Create, send, and track quotations directly from the CRM module.",
            },
            {
              icon: <FiTag size={24} />,
              title: "Customer Segmentation",
              desc: "Tag and filter customers by industry, size, region, or engagement level.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "CRM Reports & KPIs",
              desc: "Gain insights into conversion rates, sales performance, and deal aging.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Custom Workflows",
              desc: "Tailor your sales process with custom stages, statuses, and automation rules.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {item.icon}
                <h3 className="font-bold">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <h3 className="text-3xl font-bold mb-4">Grow Faster with RedERP CRM</h3>
          <p className="mb-6">Get started with a 7-day free trial. No commitment, no credit card.</p>
          <a
            href="https://app.rederp.pro/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
          >
            Try CRM Module Now
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ModuleCRM;
